import { FRAME_BYTES, FRAME_SAMPLES } from '@cixing/shared';
import {
  CLIPPING_PEAK,
  LOW_LEVEL_RMS,
  floatToPcm16,
  measureLevel,
  pcm16ToBytes,
  resampleTo16k,
} from './pcm.js';

/**
 * マイク入力の取得と、上流が要求する形式への変換。
 *
 * 重要な方針:
 *  - 音声データは React の state に絶対に入れない。毎秒数十回届くため、
 *    state に入れると字幕リスト全体が再描画され続けて実用にならない。
 *  - 音量メーターの値もコールバックで直接渡し、描画側で requestAnimationFrame に間引く。
 */

export interface MicDevice {
  deviceId: string;
  label: string;
}

export interface CaptureCallbacks {
  /** 1600バイトの PCM フレームが1つ揃うたびに呼ばれる。 */
  onFrame: (frame: ArrayBuffer) => void;
  /** 音量の更新。描画の間引きは呼び出し側の責任。 */
  onLevel: (level: { rms: number; peak: number; tooQuiet: boolean; clipping: boolean }) => void;
  onError: (message: string) => void;
}

export interface CaptureOptions {
  deviceId?: string;
  /**
   * ノイズ抑制。工場騒音では有効だが、音声の立ち上がりを削ることがあるため
   * 利用者が切り替えられるようにしてある。
   */
  noiseSuppression: boolean;
  /**
   * 小声ブースト(入力ゲイン)。1 = 増幅なし。
   * 「講師の小声が拾えない」という現地フィードバックへの対策。
   * ブラウザの AGC は控えめなため、明示的な増幅が小声の認識に効く。
   * 増幅はノイズも持ち上げるので、既定はオフ(1)にして利用者が選ぶ。
   */
  gain?: number;
}

/** マイク権限の要求。ここで拒否された場合のメッセージを日本語で返す。 */
export async function requestMicPermission(): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      // 実際に一番多い原因がこれ。LAN の http:// で開くとマイクが使えない。
      message:
        'このブラウザではマイクを使用できません。HTTPS で開いているか確認してください(http:// ではマイクが使えません)。',
    };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // 権限確認のためだけに開いたので、すぐ閉じる。
    for (const track of stream.getTracks()) track.stop();
    return { ok: true };
  } catch (error) {
    return { ok: false, message: describeMicError(error) };
  }
}

/**
 * 利用可能なマイクの一覧。
 * 権限を取る前はラベルが空になるため、必ず requestMicPermission の後で呼ぶこと。
 */
export async function listMicrophones(): Promise<MicDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'audioinput')
    .map((d, index) => ({
      deviceId: d.deviceId,
      label: d.label || `マイク ${index + 1}`,
    }));
}

export class AudioCapture {
  #context: AudioContext | undefined;
  #stream: MediaStream | undefined;
  #node: AudioWorkletNode | ScriptProcessorNode | undefined;
  #source: MediaStreamAudioSourceNode | undefined;
  /** 16kHz へ変換済みで、まだ1フレームに満たない端数。 */
  #residual = new Int16Array(0);
  #callbacks: CaptureCallbacks | undefined;

  get isRunning(): boolean {
    return this.#context !== undefined;
  }

  async start(options: CaptureOptions, callbacks: CaptureCallbacks): Promise<void> {
    this.#callbacks = callbacks;

    const constraints: MediaStreamConstraints = {
      audio: {
        channelCount: 1,
        // エコーキャンセルは相手側の音声信号がある通話向けの機能。
        // 講義の一方向録音では効果が無く、音声を削るだけなので切る。
        echoCancellation: false,
        noiseSuppression: options.noiseSuppression,
        autoGainControl: true,
        ...(options.deviceId ? { deviceId: { exact: options.deviceId } } : {}),
      },
    };

    this.#gain = options.gain ?? 1;

    try {
      this.#stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      throw new Error(describeMicError(error));
    }

    // sampleRate は指定しない。端末が対応しないレートを要求すると失敗するため、
    // 実際のレートを受け取ってから 16kHz へ変換する。
    const context = new AudioContext();
    this.#context = context;
    // 自動再生制限で suspended のまま始まることがある。
    if (context.state === 'suspended') await context.resume();

    this.#source = context.createMediaStreamSource(this.#stream);

    try {
      await context.audioWorklet.addModule('/audio-capture-worklet.js');
      const node = new AudioWorkletNode(context, 'audio-capture-processor');
      node.port.onmessage = (event: MessageEvent<{ samples: Float32Array; sampleRate: number }>) => {
        this.#handleSamples(event.data.samples, event.data.sampleRate);
      };
      this.#node = node;
      this.#source.connect(node);
      // 出力へは繋がない。繋ぐと講師の声がスピーカーから出てマイクへ回り込む。
    } catch {
      // 古い iOS Safari など AudioWorklet 非対応環境向けのフォールバック。
      this.#startScriptProcessorFallback(context);
    }
  }

  #startScriptProcessorFallback(context: AudioContext): void {
    // 非推奨 API だが、対応端末を切り捨てるよりは使う。
    const node = context.createScriptProcessor(4096, 1, 1);
    node.onaudioprocess = (event) => {
      this.#handleSamples(new Float32Array(event.inputBuffer.getChannelData(0)), context.sampleRate);
    };
    this.#node = node;
    this.#source?.connect(node);
    // ScriptProcessor は出力に繋がないとコールバックが発火しない実装がある。
    // 無音を出力するゲインを挟んで、スピーカーへは何も出さずに動かす。
    const silent = context.createGain();
    silent.gain.value = 0;
    node.connect(silent);
    silent.connect(context.destination);
  }

  #gain = 1;

  #handleSamples(samples: Float32Array, sampleRate: number): void {
    const callbacks = this.#callbacks;
    if (!callbacks) return;

    // 小声ブースト。クリップ保護のため ±1.0 で頭打ちにする
    // (このクリップは増幅後のみ起きるので、通常音量では無劣化)。
    if (this.#gain !== 1) {
      for (let i = 0; i < samples.length; i += 1) {
        const v = samples[i]! * this.#gain;
        samples[i] = v > 1 ? 1 : v < -1 ? -1 : v;
      }
    }

    // 音量は変換前の生データで測る。利用者が実際にマイクへ入れた音量そのもの。
    const level = measureLevel(samples);
    callbacks.onLevel({
      rms: level.rms,
      peak: level.peak,
      tooQuiet: level.rms < LOW_LEVEL_RMS,
      clipping: level.peak >= CLIPPING_PEAK,
    });

    const resampled = resampleTo16k(samples, sampleRate);
    const pcm = floatToPcm16(resampled);

    // 端数と連結し、1600バイト(800サンプル)ちょうどの単位で切り出す。
    const combined = new Int16Array(this.#residual.length + pcm.length);
    combined.set(this.#residual, 0);
    combined.set(pcm, this.#residual.length);

    let offset = 0;
    while (combined.length - offset >= FRAME_SAMPLES) {
      const frame = combined.subarray(offset, offset + FRAME_SAMPLES);
      const bytes = pcm16ToBytes(frame);
      if (bytes.byteLength === FRAME_BYTES) callbacks.onFrame(bytes);
      offset += FRAME_SAMPLES;
    }

    // 余りは次回へ持ち越す。捨てると音が途切れて認識精度が落ちる。
    this.#residual = combined.slice(offset);
  }

  async stop(): Promise<void> {
    const node = this.#node;
    if (node && 'port' in node) {
      try {
        node.port.postMessage('stop');
      } catch {
        // 既に閉じている場合は無視。
      }
    }

    try {
      this.#source?.disconnect();
      node?.disconnect();
    } catch {
      // 切断済みの場合は無視。
    }

    for (const track of this.#stream?.getTracks() ?? []) track.stop();

    // AudioContext を閉じないと、端末によってはマイクの使用中表示が消えない。
    try {
      await this.#context?.close();
    } catch {
      // 既に閉じている場合は無視。
    }

    this.#node = undefined;
    this.#source = undefined;
    this.#stream = undefined;
    this.#context = undefined;
    this.#residual = new Int16Array(0);
    this.#callbacks = undefined;
  }
}

/** ブラウザ差のある DOMException を、利用者が次に取るべき行動が分かる日本語にする。 */
function describeMicError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'マイクの使用が許可されませんでした。ブラウザのアドレスバーのマイク設定から許可してください。';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'マイクが見つかりません。外部マイクを接続しているか確認してください。';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'マイクを使用できません。他のアプリがマイクを使用していないか確認してください。';
    case 'OverconstrainedError':
      return '選択したマイクを使用できません。別のマイクを選んでください。';
    default:
      return 'マイクを開始できませんでした。ページを再読み込みしてお試しください。';
  }
}
